import {Box, Typography} from '@mui/material'
import type {GridColDef, GridSortModel} from '@mui/x-data-grid'
import type {CSSProperties, FC, ReactNode} from 'react'
import {useState} from 'react'
import {useGetExternalBookmarksQuery} from '@apis/base'
import BasicDataGrid from '@shared/components/base/BasicDataGrid/BasicDataGrid'
import {tablesFullPage} from '@shared/constants/lineManagement'
import {
  DEFAULT_OFFSET,
  DEFAULT_PAGE_SIZE,
  STANDARD_PAGE_SIZE_OPTIONS,
} from '@shared/constants/preferences/pagination'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {safeExternalUrl} from '@shared/utils/safeExternalUrl'

interface ExternalBookmarkLinkProps {
  url: string | null | undefined
  children: ReactNode
  className?: string
  style?: CSSProperties
  title?: string
}

/**
 * Links to a bookmark's `external_url` only when it is an absolute http(s) URL
 * (AB#6318). Anything else -- a `javascript:` or `data:` URI, a relative or
 * protocol-relative value, a missing URL -- renders the label as plain text.
 */
export const ExternalBookmarkLink: FC<ExternalBookmarkLinkProps> = ({
  url,
  children,
  className,
  style,
  title,
}) => {
  const href = safeExternalUrl(url)
  if (!href) {
    return <span className={className}>{children}</span>
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={className}
      style={style}
      title={title}
    >
      {children}
    </a>
  )
}

const ExternalBookmarkTable: FC = () => {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_OFFSET,
  })
  const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'name', sort: 'asc'}])
  const {
    data: externalBookmarks,
    isFetching: externalBookmarksIsFetching,
    error: externalBookmarksError,
  } = useGetExternalBookmarksQuery({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    ordering:
      sortModel[0]?.field && sortModel[0]?.sort
        ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
        : undefined,
  })
  const [t] = useCustomTranslation('common')

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('external_bookmarks.name'),
      editable: false,
      flex: 1,
      renderCell: (params) => (
        <ExternalBookmarkLink
          url={params.row?.external_url as string | undefined}
          className="MuiDataGrid-cellContent"
          style={{color: 'inherit', font: 'inherit', cursor: 'pointer'}}
          title={String(params.value ?? '')}
        >
          {params.value}
        </ExternalBookmarkLink>
      ),
    },
    {
      field: 'description',
      headerName: t('external_bookmarks.description'),
      editable: false,
      flex: 2,
    },
    {
      field: 'external_url',
      headerName: t('external_bookmarks.external_url'),
      editable: false,
      flex: 1,
      renderCell: (params) => (
        <ExternalBookmarkLink url={params.value as string | undefined}>
          {params.value}
        </ExternalBookmarkLink>
      ),
    },
  ]

  return (
    <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          flex: '0 0 auto',
        }}
        data-testid="external-bookmark-table-box"
      >
        <Typography variant="h2" component="h3">
          {t('external_bookmarks.title')}
        </Typography>
      </Box>
      <Box sx={{flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
        <BasicDataGrid
          data-testid="mbai-main-line-management-external-bookmarks-table"
          data-cy="mbai-main-line-management-external-bookmarks-table"
          title={String(t('external_bookmarks.title'))}
          titleSearchParams="external_bookmarks_search"
          hideHeader
          hideSearch
          height="100%"
          rows={externalBookmarks?.results || []}
          rowCount={externalBookmarks?.count || 0}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model)
            setPaginationModel((prev) => ({...prev, page: 0}))
          }}
          rowHeight={tablesFullPage.rowHeight}
          disableRowSelectionOnClick
          pageSizeOptions={STANDARD_PAGE_SIZE_OPTIONS}
          loading={externalBookmarksIsFetching}
          queryError={externalBookmarksError}
          noRowsMessage={String(t('no_data'))}
        />
      </Box>
    </Box>
  )
}

export default ExternalBookmarkTable
